import { useCallback, useEffect, useRef, useState } from 'react'
import { capKey } from './dateProtection'
import { cellParams } from './cellFilter'
import { reasonText } from './decisionReason'
import { makeClient } from 'Pages/DsRepricing/@services/http'
import { BACKEND_APIS } from '@constants/routes'

/**
 * The activity log, from the server.
 *
 * Replaces the localStorage mock that invented an event every four
 * seconds. Two things change shape, and both are the point:
 *
 * **Filtering and pagination are the server's.** The mock held every
 * row in the browser, so filtering was an `Array.filter` and the page
 * was a `slice`. One tenant already has tens of thousands of rows and
 * gains a few thousand per sweep; a screen that filters what it
 * happened to fetch finds nothing and does not say so.
 *
 * **Live is a poll, not a fabrication.** The mock minted a row on a
 * timer so the UI felt alive. This refetches the first page on the
 * same cadence, which is the same feeling and a true one. A refetch
 * only fires when the tab is visible: an operator with this open in a
 * background tab should not be asking ClickHouse anything.
 */

const http = makeClient(BACKEND_APIS.GENERAL_REPRICING_ACTIVITY_API)

const PAGE_SIZE = 30
const LIVE_INTERVAL_MS = 8000

//: Fields the server sends as strings. They are `Decimal` columns and
//: JSON has no decimal, so the wire keeps them exact — but the screen
//: renders money with `.toFixed(2)`, which a string does not have. One
//: conversion at the edge, so every renderer downstream keeps working
//: on numbers as it always did (2026-09-27).
const MONEY_FIELDS = [
	'previous_price', 'new_price', 'proposed_price', 'pushed_price', 'decided_net',
	'market_pvp', 'read_net', 'broker_markup_pct',
]

//: The same treatment for the derivation block, which arrived later
//: and carries its own money (2026-09-28).
const DERIVATION_MONEY_FIELDS = ['market_low', 'market_high', 'reference']

//: Wire values turned into something a person would say.
const CAP_TITLES = {
	markup_cap:             'Markup limit',
	price_limit:            'Price limit',
	date_protection:        'Date protection',
	rank_cap:               'Rank cap',
	keep_position:          'Keep-position guard',
	never_below_competitor: 'Never below competitor',
	never_above_competitor: 'Never above competitor',
}

function toNumbers(row) {
	const out = { ...row }
	for (const field of MONEY_FIELDS) {
		out[field] = row[field] == null ? null : Number(row[field])
	}
	// Whether anybody was ever going to send this to the PMS. Stamped
	// by the engine on every event, because an `applied` produced by a
	// simulation is not an `applied` and the log is read long after
	// the run (2026-09-28). Absent on rows written before the stamp
	// existed — unknown, which is not the same as live.
	out.mode = row.payload?.mode || null
	out.simulated = out.mode === 'dry_run'

	// Why nothing happened. The engine writes it into the payload and
	// nothing lifted it out, so the Reason block — the entire content
	// of a `no_price_change` or `no_strategy` event — rendered a dash
	// (Curro, 2026-09-28). Most are sentences already; the two the
	// policies emit are codes, and a code is not a reason.
	out.reason = reasonText(row.payload?.reason)
	// Which rate-card face the decision was for. Every priced event has
	// carried it; since CU-869fe6mf4 a `no_strategy` does too when the cell
	// has faces, which is what tells "nobody covers this cell" from "nobody
	// covers this face". The screen names it through the faces list
	// (`@services/faceScope`); absent on rows written before faces existed.
	out.face_id = row.payload?.face_id == null || row.payload?.face_id === '' ? null : String(row.payload.face_id)

	// The screen learnt these names from the mock. `capped_to` is the net
	// after the cap — pushed when the row was sent, the price we would
	// push otherwise (`pushed_price` is null until the write log says a
	// write landed, CU-869f03vek) — and `limit.value` is the number that
	// did the capping — the last layer of the chain.
	out.capped_to = out.pushed_price ?? out.new_price
	const chain = Array.isArray(row.cap_chain) ? row.cap_chain : []
	const lastCap = chain[chain.length - 1]
	if (lastCap) {
		out.limit = {
			id: lastCap.source?.price_limit_id || lastCap.reason,
			// `reason` is a wire value — `price_limit`, `markup_cap`.
			// Showing it raw put a snake-cased token where the box
			// promised the name of the limit that fired (2026-09-28).
			label: lastCap.source?.label || CAP_TITLES[capKey(lastCap)] || lastCap.reason,
			kind: capKey(lastCap),
			value: Number(lastCap.source?.limit_value ?? lastCap.to) || null,
		}
	}
	// What actually bound this decision, for the row and the filters. A
	// chain can hold both a markup cap and a price limit, so this is a
	// list and not a second event type — the event is one decision.
	// Keyed, not by reason: a date protection keeps `reason =
	// price_limit` and is told apart by `source.kind` (CU-869fe8jrk).
	out.capped_by = [...new Set(chain.map(capKey))]
	// What the quarantine measured, and from where. A protected cell
	// carries a baseline whether or not it was held.
	out.quarantine = row.payload?.quarantine || null
	out.quarantine_baseline = row.payload?.quarantine_baseline || null
	// Who resolved it and when (CU-869fdg4nu): the cell says
	// "Override · Curro · 12:04" instead of a bare state.
	out.resolution_actor = row.resolution_actor || null
	out.resolution_at = row.resolution_at || null
	out.resolution_note = row.resolution_note || null
	// Every write of this decision by origin, oldest first; the flat
	// fields are the latest one's. After an override there are two
	// (CU-869fdg4nu). Named `writes` since CU-869fhuhy6: `sends` is now
	// the N pricelists of one decision (below) — a rename, two readers.
	out.writes = Array.isArray(row.writes) ? row.writes : []
	// One decision, N sends (CU-869fhuhy6, § 4.4): the product the
	// decision was for, the sites that took part (`sites`), how many were
	// expected before the flush closed (`sites_expected`), one send per
	// pricelist of the product (`sends`), their summary, and whether an
	// override may push the pre-cap price (`overridable` = some send was
	// bound by its cap, TL C3). Absent on rows written before the products.
	out.product = row.product && typeof row.product === 'object' ? row.product : null
	out.sites = Array.isArray(row.sites) ? row.sites : []
	out.sites_expected = row.sites_expected == null ? null : Number(row.sites_expected)
	out.sends = Array.isArray(row.sends) ? row.sends.map(s => ({
		...s,
		markup_pct: s.markup_pct == null ? null : Number(s.markup_pct),
		cap_pct: s.cap_pct == null ? null : Number(s.cap_pct),
		amount: s.amount == null ? null : Number(s.amount),
	})) : []
	out.sends_summary = row.sends_summary && typeof row.sends_summary === 'object' ? row.sends_summary : null
	out.overridable = row.overridable == null ? null : !!row.overridable
	// Brokers compete (CU-869ffwj71): another strategy took this
	// bracket, and the row says by whom — `write_status` is
	// `outranked` and this block carries names, never ids. On the winner,
	// `won_over` lists the decisions it beat; empty when it had no rival.
	out.outranked_by = row.outranked_by && typeof row.outranked_by === 'object' ? row.outranked_by : null
	out.won_over = Array.isArray(row.won_over) ? row.won_over : []

	// The derivation, on the same terms: the server sends its money as
	// strings for the same reason, and the rail renders with `.toFixed`.
	if (row.derivation) {
		const d = { ...row.derivation }
		for (const field of DERIVATION_MONEY_FIELDS) {
			d[field] = d[field] == null ? null : Number(d[field])
		}
		d.steps = (d.steps || []).map(step => ({
			...step,
			from: step.from == null ? null : Number(step.from),
			to:   step.to   == null ? null : Number(step.to),
		}))
		// Our own rows (CU-869fh646w): the one taken and the ones left
		// aside, their money as strings like everything else here.
		const money = (v) => (v == null ? null : Number(v))
		if (d.own && typeof d.own === 'object') {
			d.own = { ...d.own, pvp: money(d.own.pvp), net: money(d.own.net) }
		}
		if (Array.isArray(d.own_discarded)) {
			d.own_discarded = d.own_discarded.filter(Boolean).map(r => ({ ...r, pvp: money(r.pvp) }))
		}
		out.derivation = d
	}
	return out
}

function query({ filters, page, pageSize }) {
	const q = new URLSearchParams()
	q.set('page', String(page))
	q.set('page_size', String(pageSize))
	if (filters.event_types?.length) q.set('event_type', filters.event_types.join(','))
	// Which kind of cap bound the decision. Not an event type: one
	// decision can be bound by a markup cap and a price limit at once,
	// so it narrows `capped` rather than replacing it (2026-09-28).
	if (filters.capped_by?.length) q.set('capped_by', filters.capped_by.join(','))
	// A flag: sent only when on, so the server never has to read it as
	// "show me the ones that are NOT blocked", which nobody wants.
	if (filters.blocked_by_floor) q.set('blocked_by_floor', '1')
	if (filters.tether_vs_order) q.set('tether_vs_order', '1')
	if (filters.incomplete_market) q.set('incomplete_market', '1')
	// Decided and never sent: the gap between the event and the write
	// log, since the push waits for the search to close (CU-869fb9wz6).
	if (filters.decided_not_sent) q.set('decided_not_sent', '1')
	// The decisions another strategy outranked on their bracket: a
	// subset of the ones above, with a facet of its own (CU-869ffwj71).
	if (filters.outranked) q.set('outranked', '1')
	// The product the decision was for, and how its sends went
	// (CU-869fhuhy6): `capped_on_some_site`, `refused_on_some_site`,
	// `late_not_applied` — facets over the write log.
	if (filters.product_id) q.set('product_id', filters.product_id)
	if (filters.send_status) q.set('send_status', filters.send_status)
	// Setup edits are off unless asked for: the feed answers "what did
	// the engine do", and an edit is a different question (2026-09-28).
	if (filters.include_setup) q.set('include_setup', '1')
	if (filters.office && filters.office !== '*') q.set('office', filters.office)
	// One pass of the sweep, when the screen was opened from Executions.
	if (filters.execution_id) q.set('execution_id', filters.execution_id)
	if (filters.sipp && filters.sipp !== '*') q.set('sipp', filters.sipp)
	if (filters.source) q.set('source', filters.source)
	if (filters.from) q.set('date_from', filters.from)
	if (filters.to) q.set('date_to', filters.to)
	// The cell — pickup date and duration (CU-869fhtp3a). `cellParams` is
	// the one translation to the four wire names; the URL's `duration=7`
	// leaves here as `duration_from=7&duration_to=7`.
	for (const [k, v] of Object.entries(cellParams(filters))) q.set(k, v)
	if (filters.search) q.set('q', filters.search)
	return q.toString()
}

export function useActivityFeed({ filters, page, pageSize = PAGE_SIZE, live }) {
	const [state, setState] = useState({ loading: true, items: [], total: 0 })
	// The request in flight, so a slow answer cannot overwrite a newer
	// one — typing in the search box fires one per keystroke.
	const latest = useRef(0)

	const load = useCallback(async ({ quiet = false } = {}) => {
		const ticket = ++latest.current
		if (!quiet) setState(s => ({ ...s, loading: true }))
		try {
			const body = await http.get(`/activity?${query({ filters, page, pageSize })}`)
			if (ticket !== latest.current) return
			setState({ loading: false, items: (body?.items || []).map(toNumbers),
			           total: body?.total || 0, error: null })
		} catch (e) {
			if (ticket !== latest.current) return
			setState({ loading: false, items: [], total: 0,
			           error: e?.message || 'Could not load the activity log' })
		}
	}, [JSON.stringify(filters), page, pageSize])

	useEffect(() => { load() }, [load])

	useEffect(() => {
		if (!live) return
		const tick = () => {
			if (document.visibilityState === 'visible') load({ quiet: true })
		}
		const t = setInterval(tick, LIVE_INTERVAL_MS)
		return () => clearInterval(t)
	}, [live, load])

	return { ...state, reload: load, pageSize }
}

/**
 * `{event_type: n}` over the whole log, for the headline counters.
 *
 * Separate from the feed because it must NOT follow the filters: the
 * counters say what exists, and a counter that moves when you filter
 * is a counter nobody can use to decide what to filter by. The window
 * (`from`) and the cell (`cell`: pickup date and duration, CU-869fhtp3a,
 * D6) are the exceptions — they are not facets but the question itself:
 * "what happened with 31 Dec · 7 days" has tiles of its own.
 */
export function useActivityCounts({ from, cell, live }) {
	// `{counts, capCounts}` — event types and, beside them, how many
	// events each kind of cap bound. One request: they are drawn
	// together and a second would make half the chips appear late.
	// `loaded` is false until the first answer (or failure): the screen draws its skeleton until then
	// instead of a row of zeros.
	const [counts, setCounts] = useState({ counts: {}, capCounts: {}, blockedByFloor: 0, tetherVsOrder: 0,
	                                       incompleteMarket: 0, quarantinedPending: 0, outranked: null, sends: null,
	                                       loaded: false })

	const load = useCallback(async () => {
		try {
			const q = new URLSearchParams()
			if (from) q.set('date_from', from)
			for (const [k, v] of Object.entries(cellParams(cell || {}))) q.set(k, v)
			const s = q.toString()
			const body = await http.get(`/activity/counts${s ? `?${s}` : ''}`)
			setCounts({ counts: body?.counts || {},
			            capCounts: body?.cap_counts || {},
			            // Its own number, not a cap: these are the
			            // events where the ladder could NOT be restored.
			            blockedByFloor: body?.blocked_by_floor || 0,
			            tetherVsOrder: body?.tether_vs_order || 0,
			            incompleteMarket: body?.incomplete_market || 0,
			            // Held decisions nobody has acted on, counted by the
			            // server across the whole log — not by the page.
			            quarantinedPending: body?.quarantined_pending || 0,
			            // Decisions, not brackets. `null` until the server
			            // says: the facet shows no number rather than a "0"
			            // that would mean "unknown" (UX, CU-869ffwj71).
			            outranked: body?.outranked ?? null,
			            // Pricelists written in the window (CU-869fhuhy6): a
			            // decision is one row however many sends it has, so
			            // the tile beside "Applied" says how many went out.
			            // `null` until the server gives one.
			            sends: body?.sends ?? null, loaded: true })
		} catch {
			setCounts({ counts: {}, capCounts: {}, blockedByFloor: 0, tetherVsOrder: 0, incompleteMarket: 0,
			            quarantinedPending: 0, outranked: null, sends: null, loaded: true })
		}
	}, [from, JSON.stringify(cell || {})])

	useEffect(() => { load() }, [load])
	useEffect(() => {
		if (!live) return
		const t = setInterval(() => {
			if (document.visibilityState === 'visible') load()
		}, LIVE_INTERVAL_MS)
		return () => clearInterval(t)
	}, [live, load])

	return counts
}

/**
 * A human acting on a quarantined decision. Both go to webback, which
 * asks the engine; the engine appends the resolution row. A 409 comes
 * back with a `code` — `simulation_mode`, `already_resolved` — and is
 * the operator's answer, not an outage.
 */
export const activityActions = {
	/** The decisions of one pass and one outcome, newest first — what the
	 *  Executions popup lists. Capped at the server's page maximum. */
	/** Decisions for a popup, biggest change first: `params` are the
	 *  server's own filter names (`execution_id`, `event_type`,
	 *  `date_from`/`date_to`, `decided_not_sent`). */
	listDecisions: (params, pageSize = 200) => {
		const q = new URLSearchParams({ page: '1', page_size: String(pageSize), order: 'delta' })
		for (const [k, v] of Object.entries(params || {})) if (v != null && v !== '') q.set(k, String(v))
		return http.get(`/activity?${q.toString()}`).then(b => ({
			...b, items: (b?.items || []).map(toNumbers) }))
	},
	listByExecution: (executionId, eventType, pageSize = 200) =>
		activityActions.listDecisions({ execution_id: executionId, event_type: eventType || undefined }, pageSize),
	/** The brackets of one PMS call — what each one carried and where it came from. */
	call: (callId) => http.get(`/activity/calls/${encodeURIComponent(callId)}`),
	approve: (id, reason) => http.post(`/activity/${id}/approve`, { reason: reason || null }),
	/** What an override would push — nothing is written (CU-869fdg4nu). */
	overridePreview: (id) => http.post(`/activity/${id}/override/preview`, {}),
	/** Push the pre-cap price over a capped decision, for this one hit. */
	override: (id, reason) => http.post(`/activity/${id}/override`, { reason }),
	discard: (id, reason) => http.post(`/activity/${id}/discard`, { reason: reason || null }),
}
